import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "../styles/bubbleModal.css";

export type BubbleStep = {
  selector: string;
  title?: string;
  text: string;
  placement?: "top" | "bottom" | "left" | "right";
};

type BubblePosition = {
  top: number;
  left: number;
};

type BubbleModalContextType = {
  showBubble: (steps: BubbleStep[]) => void;
  closeBubble: () => void;
};

const BubbleModalContext = createContext<BubbleModalContextType | undefined>(
  undefined,
);

export const BubbleModalProvider = ({ children }: { children: ReactNode }) => {
  const [steps, setSteps] = useState<BubbleStep[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<BubblePosition>({
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
  });

  const activeStep = steps[activeIndex];

  useEffect(() => {
    if (steps.length > 0) {
      document.body.classList.add("bubble-tour-active");
    } else {
      document.body.classList.remove("bubble-tour-active");
    }

    return () => {
      document.body.classList.remove("bubble-tour-active");
    };
  }, [steps.length]);

  const clearTourHighlights = () => {
    document.querySelectorAll(".bubble-tour-highlight").forEach((el) => {
      el.classList.remove("bubble-tour-highlight");
      el.removeAttribute("data-bubble-tour-label");
    });
  };

  const closeBubble = () => {
    clearTourHighlights();
    setSteps([]);
    setActiveIndex(0);
  };

  const calculatePosition = () => {
    if (!activeStep) return;

    clearTourHighlights();

    const target = document.querySelector(activeStep.selector) as HTMLElement;

    if (!target) {
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      });
      return;
    }

    const currentScrollX = window.scrollX;

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    window.scrollTo({
      top: window.scrollY,
      left: currentScrollX,
      behavior: "instant" as ScrollBehavior,
    });

    setTimeout(() => {
      const rect = target.getBoundingClientRect();
      const placement = activeStep.placement || "bottom";

      target.classList.add("bubble-tour-highlight");
      target.setAttribute(
        "data-bubble-tour-label",
        activeStep.title || "Tour Guide",
      );

      let top = rect.bottom + 14;
      let left = rect.left + rect.width / 2;

      if (placement === "top") {
        top = rect.top - 14;
        left = rect.left + rect.width / 2;
      }

      if (placement === "left") {
        top = rect.top + rect.height / 2;
        left = rect.left - 14;
      }

      if (placement === "right") {
        top = rect.top + rect.height / 2;
        left = rect.right + 14;
      }

      const safeGap = 18;
      const bubbleWidth = 310;
      const bubbleHeight = 130;

      const minLeft = safeGap + bubbleWidth / 2;
      const maxLeft = window.innerWidth - safeGap - bubbleWidth / 2;

      const minTop = safeGap;
      const maxTop = window.innerHeight - safeGap - bubbleHeight;

      left = Math.max(minLeft, Math.min(left, maxLeft));
      top = Math.max(minTop, Math.min(top, maxTop));

      window.scrollTo({
        top: window.scrollY,
        left: 0,
        behavior: "instant" as ScrollBehavior,
      });

      setPosition({
        top,
        left,
      });
    }, 350);
  };

  useEffect(() => {
    calculatePosition();

    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);

    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [activeIndex, activeStep?.selector]);

  const showBubble = (newSteps: BubbleStep[]) => {
    if (!newSteps.length) return;

    setSteps(newSteps);
    setActiveIndex(0);
  };

  const handleNext = () => {
    if (activeIndex >= steps.length - 1) {
      closeBubble();
      return;
    }

    setActiveIndex((prev) => prev + 1);
  };

  const contextValue = useMemo(
    () => ({
      showBubble,
      closeBubble,
    }),
    [],
  );

  return (
    <BubbleModalContext.Provider value={contextValue}>
      {children}

      {activeStep && (
        <div className="bubble-tour-layer">
          <div
            className={`bubble-tour-box bubble-tour-${activeStep.placement || "bottom"}`}
            style={{
              top: position.top,
              left: position.left,
            }}
          >
            {activeStep.title && (
              <strong className="bubble-tour-title">{activeStep.title}</strong>
            )}
            <p>{activeStep.text}</p>

            <div className="bubble-tour-actions">
              {activeIndex < steps.length - 1 ? (
                <>
                  <button
                    type="button"
                    className="bubble-tour-skip-btn"
                    onClick={closeBubble}
                  >
                    Skip
                  </button>

                  <button
                    type="button"
                    className="bubble-tour-next-btn"
                    onClick={handleNext}
                  >
                    Next
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="bubble-tour-next-btn"
                  onClick={closeBubble}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </BubbleModalContext.Provider>
  );
};

export const useBubbleModal = () => {
  const context = useContext(BubbleModalContext);

  if (!context) {
    throw new Error("useBubbleModal must be used inside BubbleModalProvider");
  }

  return context;
};
